import { CommonServerSideParams } from '@/app/types/CommonServerSideParams';
import { OnlyBrowserPageProps } from '@/layouts/core/types/OnlyBrowserPageProps';
import { SSGPageProps } from '@/layouts/core/types/SSGPageProps';
import DefaultLayout from '@/layouts/public/components/PublicLayout';
import {
  getPublicLayoutStaticPaths,
  getPublicLayoutStaticProps,
} from '@/layouts/public/publicLayoutSSG';
import ChatbotDialog from '@/modules/chatbot/components/ChatbotDialog';
import { AMPLITUDE_PAGES } from '@/modules/core/amplitude/events';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import useCustomer from '@/modules/core/data/hooks/useCustomer';
import { Customer } from '@/modules/core/data/types/Customer';
import { createLogger } from '@/modules/core/logging/logger';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  GetStaticPaths,
  GetStaticProps,
  NextPage,
} from 'next';
// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
import React, { Fragment } from 'react';

const fileLabel = 'pages/[locale]/chatbot';
const logger = createLogger({ // eslint-disable-line no-unused-vars,@typescript-eslint/no-unused-vars
  fileLabel,
});

/**
 * Only executed on the server side at build time
 * Necessary when a page has dynamic routes and uses "getStaticProps"
 */
export const getStaticPaths: GetStaticPaths<CommonServerSideParams> = getPublicLayoutStaticPaths;

/**
 * Only executed on the server side at build time.
 *
 * @return Props (as "SSGPageProps") that will be passed to the Page component, as props
 *
 * @see https://github.com/vercel/next.js/discussions/10949#discussioncomment-6884
 * @see https://nextjs.org/docs/basic-features/data-fetching#getstaticprops-static-generation
 */
export const getStaticProps: GetStaticProps<SSGPageProps, CommonServerSideParams> = getPublicLayoutStaticProps;

/**
 * SSG pages are first rendered by the server (during static bundling)
 * Then, they're rendered by the client, and gain additional props (defined in OnlyBrowserPageProps)
 * Because this last case is the most common (server bundle only happens during development stage), we consider it a default
 * To represent this behaviour, we use the native Partial TS keyword to make all OnlyBrowserPageProps optional
 *
 * Beware props in OnlyBrowserPageProps are not available on the server
 */
type Props = {} & SSGPageProps<Partial<OnlyBrowserPageProps>>;

/**
 * Homepage of the TFP website/platform.
 *
 * Dynamically displays the modules, based on whether they've been activated.
 * Doesn't do anything complicated, basically displays a few animations and links towards the other modules.
 */
const ChatbotPage: NextPage<Props> = (props): JSX.Element => {
  const customer: Customer = useCustomer();
  const { modTFP } = customer;
  const {
    serviceLabel,
  } = modTFP || {};
  const { isInIframe } = props;

  return (
    <DefaultLayout
      {...props}
      pageName={AMPLITUDE_PAGES.CHATBOT_PAGE}
      headProps={{
        seoTitle: serviceLabel,
      }}
    >
      <Amplitude>
        {
          ({ logEvent }: { logEvent: LogEvent }): JSX.Element => {
            return (
              <Fragment>
                <ChatbotDialog
                  isInIframe={isInIframe}
                />
              </Fragment>
            );
          }
        }
      </Amplitude>
    </DefaultLayout>
  );
};

export default (ChatbotPage);
